import { useCallback, useEffect, useState } from "react"
import { StyleProvider } from "@ant-design/cssinjs"
import { useSplitScreenStore } from "store/splitScreen.store"
import { safeRender, type RootEntry } from "../core/shadowPortal"
import { QuotationContextMenu } from "../QuotationContextMenu/QuotationContextMenu"
import type { TContextMenuItem } from "../QuotationContextMenu/QuotationContextMenu"
import type { Dispatch, MutableRefObject, SetStateAction } from "react"
import type { WebViewerInstance } from "@pdftron/webviewer"
import type { TEcall, TErefWithFurniture, TFurniture } from "types/elevationReferences.type"
import type { TViewerPane } from "services/annotations.service"
import type { TDeleteConfirmTarget } from "../quotationViewer.helpers"
import type { useFurnitureClipboard } from "./useFurnitureClipboard"
import type { useBatchOperations } from "./useBatchOperations"
import type { useErefStatusChange } from "./useErefStatusChange"

type TClipboard = ReturnType<typeof useFurnitureClipboard>
type TBatchOps = ReturnType<typeof useBatchOperations>

type TContextMenuKind = "furniture" | "furniture-batch" | "eref" | "ecall" | "free"

type TContextMenuCtx = {
	getShadowRoot: () => ShadowRoot | undefined
	instanceRef: MutableRefObject<WebViewerInstance | null>
	jumpingNavTargetContainerRef: MutableRefObject<HTMLElement | null>
	// The overlay root is created by createOverlayContainers and owned (for disposal) by the
	// orchestrator; the target refs are written by the orchestrator's contextmenu handler and
	// read here when building the menu items for the matching kind.
	contextMenuEntryRef: MutableRefObject<RootEntry | null>
	contextMenuFurnitureRef: MutableRefObject<TFurniture | null>
	contextMenuBatchRef: MutableRefObject<TFurniture[]>
	// The eref a right-click landed on, for the single-eref menu (REQ-376 Delete).
	contextMenuErefRef: MutableRefObject<TErefWithFurniture | null>
	// The ecall a right-click landed on, for the single-ecall menu (REQ-374).
	contextMenuEcallRef: MutableRefObject<TEcall | null>
	contextMenuPointRef: MutableRefObject<{ pageNumber: number; x: number; y: number } | null>
	copyFurnitureToClipboard: TClipboard["copyFurnitureToClipboard"]
	duplicateFurnitureInPlace: TClipboard["duplicateFurnitureInPlace"]
	pasteFurnitureAtPoint: TClipboard["pasteFurnitureAtPoint"]
	openConnectModal: TBatchOps["openConnectModal"]
	getReviewToggle: TBatchOps["getReviewToggle"]
	runBatchStatus: TBatchOps["runBatchStatus"]
	runDetach: TBatchOps["runDetach"]
	runErefStatus: ReturnType<typeof useErefStatusChange>["runErefStatus"]
	startConnectObjects: (eref: TErefWithFurniture) => void
	// Resolves the connected-elevation rows for the "Navigate to elevation" submenu (page_name).
	findErefWithFurnitureById: (id: number) => TErefWithFurniture | undefined
	// Reuses the sidebar's own per-elevation navigation, chosen by which pane the right-click landed
	// in — exactly the split the primary/split EcallSidebar instances already use.
	onNavigateToEref: (erefId: number, pane: TViewerPane) => void
	setDeleteConfirm: Dispatch<SetStateAction<TDeleteConfirmTarget | null>>
	batchPending: boolean
}

// Owns the right-click context menu (Copy / Duplicate / Connect·Move / Delete on a single
// furniture, the batch "Menubar" on a multi-selection, and Paste on empty space). The orchestrator
// writes the target refs from its `contextmenu` handler then calls `openContextMenuAt`; this hook
// holds the position state and renders the menu into the shared shadow-DOM overlay, plus the
// dismiss (outside click / scroll / zoom / Escape) wiring.
export const useContextMenu = ({
	getShadowRoot,
	instanceRef,
	jumpingNavTargetContainerRef,
	contextMenuEntryRef,
	contextMenuFurnitureRef,
	contextMenuBatchRef,
	contextMenuErefRef,
	contextMenuEcallRef,
	contextMenuPointRef,
	copyFurnitureToClipboard,
	duplicateFurnitureInPlace,
	pasteFurnitureAtPoint,
	openConnectModal,
	getReviewToggle,
	runBatchStatus,
	runDetach,
	runErefStatus,
	startConnectObjects,
	findErefWithFurnitureById,
	onNavigateToEref,
	setDeleteConfirm,
	batchPending,
}: TContextMenuCtx) => {
	// Re-binds the dismiss listeners after split screen re-mounts the panes (REQ-399).
	const layoutEpoch = useSplitScreenStore((state) => state.layoutEpoch)
	// Position is in app-container-relative px. The furniture/point the menu acts on are held in
	// refs (passed in) so the menu's item handlers read the right target without re-subscribing.
	const [contextMenu, setContextMenu] = useState<{
		left: number
		top: number
		kind: TContextMenuKind
		pane: TViewerPane
	} | null>(null)

	const closeContextMenu = useCallback(() => {
		contextMenuFurnitureRef.current = null
		contextMenuBatchRef.current = []
		contextMenuErefRef.current = null
		contextMenuEcallRef.current = null
		contextMenuPointRef.current = null
		setContextMenu(null)
	}, [contextMenuFurnitureRef, contextMenuBatchRef, contextMenuErefRef, contextMenuEcallRef, contextMenuPointRef])

	const openContextMenuAt = useCallback(
		(clientX: number, clientY: number, kind: TContextMenuKind, pane: TViewerPane = "primary") => {
			const appEl = jumpingNavTargetContainerRef.current
			if (!appEl) return
			const rect = appEl.getBoundingClientRect()
			setContextMenu({ left: clientX - rect.left, top: clientY - rect.top, kind, pane })
		},
		[jumpingNavTargetContainerRef],
	)

	// Full action set for the dark right-click menu on a multi-furniture selection (Figma
	// "Menubar"). Reuses the same handlers as the pill and adds Detach / Mark as unreviewed.
	const buildBatchMenuItems = useCallback(
		(furniture: TFurniture[]): TContextMenuItem[] => {
			const anyUnconnected = furniture.some((f) => f.eref_id == null)
			const anyConnected = furniture.some((f) => f.eref_id != null)
			const samePage = new Set(furniture.map((f) => f.page_number)).size === 1

			const items: TContextMenuItem[] = [
				{
					key: "connect",
					label: anyUnconnected ? "Connect to elevation" : "Move to another elevation",
					disabled: batchPending || !samePage,
					onSelect: () => {
						closeContextMenu()
						openConnectModal(furniture)
					},
				},
			]
			const reviewToggle = getReviewToggle(furniture)
			if (reviewToggle) {
				items.push({
					key: reviewToggle.key,
					label: reviewToggle.label,
					disabled: batchPending,
					onSelect: () => {
						closeContextMenu()
						void runBatchStatus(furniture, reviewToggle.targetStatus)
					},
				})
			}
			if (anyConnected) {
				items.push({
					key: "detach",
					label: "Detach from elevation",
					disabled: batchPending,
					onSelect: () => {
						closeContextMenu()
						void runDetach(furniture)
					},
				})
			}
			items.push({
				key: "delete",
				label: "Delete",
				disabled: batchPending,
				onSelect: () => {
					closeContextMenu()
					setDeleteConfirm({ kind: "furniture", furniture })
				},
			})
			return items
		},
		[batchPending, openConnectModal, getReviewToggle, runBatchStatus, runDetach, closeContextMenu, setDeleteConfirm],
	)

	// Menu for a single eref: "Connect objects" (REQ-372) always first — it is available in every
	// state and whether or not the eref already has objects — then the BACKWARD state transition
	// (REQ-373): "Mark as unreviewed" for a reviewed eref, "Remove from quotation" for one in
	// quotation, nothing for work_in_progress (it has none) or for an eref with no connected objects.
	// Delete (REQ-376) closes the menu and is likewise available in every state. Labels and keys
	// mirror the furniture menu's.
	const buildErefMenuItems = useCallback(
		(eref: TErefWithFurniture | null): TContextMenuItem[] => {
			if (!eref) return []
			const status = eref.status ?? "work_in_progress"
			const items: TContextMenuItem[] = [
				{
					key: "connectObjects",
					label: "Connect objects",
					disabled: batchPending,
					onSelect: () => {
						closeContextMenu()
						startConnectObjects(eref)
					},
				},
			]

			if (eref.furniture.length > 0) {
				if (status === "reviewed") {
					items.push({
						key: "unreview",
						label: "Mark as unreviewed",
						separatorBefore: true,
						disabled: batchPending,
						onSelect: () => {
							closeContextMenu()
							void runErefStatus(eref, "work_in_progress")
						},
					})
				} else if (status === "in_quotation") {
					items.push({
						key: "removeQuotation",
						label: "Remove from quotation",
						separatorBefore: true,
						disabled: batchPending,
						onSelect: () => {
							closeContextMenu()
							void runErefStatus(eref, "reviewed")
						},
					})
				}
			}

			items.push({
				key: "delete",
				label: "Delete",
				shortcut: "⌫",
				// "Connect objects" is always above, so Delete always closes its own group.
				separatorBefore: true,
				disabled: batchPending,
				onSelect: () => {
					closeContextMenu()
					setDeleteConfirm({ kind: "eref", eref })
				},
			})
			return items
		},
		[batchPending, runErefStatus, startConnectObjects, closeContextMenu, setDeleteConfirm],
	)

	// Menu for a single ecall (REQ-374): "Navigate to elevation" — a submenu listing every connected
	// elevation reference as "{elevation_number} – {page_name}", exactly the label + click behavior
	// the ecall sidebar's own rows already use — omitted entirely when the ecall has no connected
	// erefs. Delete always follows, mirroring the eref menu's shortcut/kind shape.
	const buildEcallMenuItems = useCallback(
		(ecall: TEcall | null, pane: TViewerPane): TContextMenuItem[] => {
			if (!ecall) return []
			const connectedElevations = ecall.elevation_numbers.filter(
				(en): en is typeof en & { eref_id: number } => en.eref_id != null,
			)

			const items: TContextMenuItem[] = []
			if (connectedElevations.length > 0) {
				items.push({
					key: "navigate",
					label: "Navigate to elevation",
					children: connectedElevations.map((en) => {
						const eref = findErefWithFurnitureById(en.eref_id)
						const label = eref?.page_name ? `${en.elevation_number} – ${eref.page_name}` : en.elevation_number
						return {
							key: `eref-${en.eref_id}-${en.id}`,
							label,
							onSelect: () => {
								closeContextMenu()
								onNavigateToEref(en.eref_id, pane)
							},
						}
					}),
				})
			}

			items.push({
				key: "delete",
				label: "Delete",
				shortcut: "⌫",
				separatorBefore: items.length > 0,
				disabled: batchPending,
				onSelect: () => {
					closeContextMenu()
					setDeleteConfirm({ kind: "ecall", ecall })
				},
			})
			return items
		},
		[batchPending, findErefWithFurnitureById, onNavigateToEref, closeContextMenu, setDeleteConfirm],
	)

	// Dismiss the context menu on outside click, scroll, zoom, or Escape. Scroll/zoom are listened
	// to on EVERY pane (split screen — REQ-399), re-bound on `layoutEpoch` because entering split
	// detaches the scroll element captured before it.
	useEffect(() => {
		if (!contextMenu) return
		const onOutsidePointerDown = (e: Event) => {
			const menuEl = contextMenuEntryRef.current?.container
			const path = (e as MouseEvent).composedPath?.() ?? []
			if (menuEl && path.includes(menuEl)) return
			closeContextMenu()
		}
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") closeContextMenu()
		}
		const instance = instanceRef.current
		const viewers = instance ? (instance.Core.getDocumentViewers?.() ?? [instance.Core.documentViewer]) : []
		const bindings = viewers.filter(Boolean).map((dv) => {
			const scrollEl = (dv.getScrollViewElement?.() as HTMLElement | null) ?? null
			scrollEl?.addEventListener("scroll", closeContextMenu, { passive: true })
			dv.addEventListener("zoomUpdated", closeContextMenu)
			return () => {
				scrollEl?.removeEventListener("scroll", closeContextMenu)
				dv.removeEventListener("zoomUpdated", closeContextMenu)
			}
		})
		window.addEventListener("mousedown", onOutsidePointerDown, true)
		window.addEventListener("keydown", onKey)
		return () => {
			window.removeEventListener("mousedown", onOutsidePointerDown, true)
			window.removeEventListener("keydown", onKey)
			bindings.forEach((unbind) => unbind())
		}
	}, [contextMenu, closeContextMenu, instanceRef, contextMenuEntryRef, layoutEpoch])

	// Render the context menu into its shadow-DOM overlay at the stored position.
	useEffect(() => {
		const entry = contextMenuEntryRef.current
		if (!entry) return
		const shadowRoot = getShadowRoot()
		if (!contextMenu || !shadowRoot) {
			entry.container.style.display = "none"
			safeRender(contextMenuEntryRef, null)
			return
		}

		entry.container.style.left = `${contextMenu.left}px`
		entry.container.style.top = `${contextMenu.top}px`
		entry.container.style.display = "block"

		let items: TContextMenuItem[]
		if (contextMenu.kind === "furniture-batch") {
			items = buildBatchMenuItems(contextMenuBatchRef.current)
		} else if (contextMenu.kind === "eref") {
			items = buildErefMenuItems(contextMenuErefRef.current)
		} else if (contextMenu.kind === "ecall") {
			items = buildEcallMenuItems(contextMenuEcallRef.current, contextMenu.pane)
		} else if (contextMenu.kind === "furniture") {
			const furniture = contextMenuFurnitureRef.current
			items = [
				{
					key: "copy",
					label: "Copy",
					shortcut: "⌘C",
					onSelect: () => {
						const f = contextMenuFurnitureRef.current
						closeContextMenu()
						if (f) copyFurnitureToClipboard(f)
					},
				},
				{
					key: "duplicate",
					label: "Duplicate",
					shortcut: "⌘D",
					onSelect: () => {
						const f = contextMenuFurnitureRef.current
						closeContextMenu()
						if (f) void duplicateFurnitureInPlace(f)
					},
				},
				{
					key: "connect",
					// Unconnected → link it; already connected → relink to a different elevation.
					label: furniture?.eref_id == null ? "Connect to elevation" : "Move to another elevation",
					separatorBefore: true,
					disabled: batchPending,
					onSelect: () => {
						const f = contextMenuFurnitureRef.current
						closeContextMenu()
						if (f) openConnectModal([f])
					},
				},
				{
					key: "delete",
					label: "Delete",
					shortcut: "⌫",
					separatorBefore: true,
					disabled: batchPending,
					onSelect: () => {
						const f = contextMenuFurnitureRef.current
						closeContextMenu()
						if (f) setDeleteConfirm({ kind: "furniture", furniture: [f] })
					},
				},
			]
		} else {
			items = [
				{
					key: "paste",
					label: "Paste",
					shortcut: "⌘V",
					onSelect: () => {
						const point = contextMenuPointRef.current
						closeContextMenu()
						if (point) void pasteFurnitureAtPoint(point)
					},
				},
			]
		}

		safeRender(
			contextMenuEntryRef,
			<StyleProvider container={shadowRoot}>
				<QuotationContextMenu items={items} />
			</StyleProvider>,
		)
	}, [
		contextMenu,
		getShadowRoot,
		copyFurnitureToClipboard,
		pasteFurnitureAtPoint,
		duplicateFurnitureInPlace,
		buildBatchMenuItems,
		buildErefMenuItems,
		buildEcallMenuItems,
		closeContextMenu,
		batchPending,
		openConnectModal,
		setDeleteConfirm,
		contextMenuEntryRef,
		contextMenuBatchRef,
		contextMenuFurnitureRef,
		contextMenuErefRef,
		contextMenuEcallRef,
		contextMenuPointRef,
	])

	return { closeContextMenu, openContextMenuAt }
}
